/* eslint-disable react/no-unknown-property */
import { FC, useMemo, useState } from 'react';

import {
  TestSurface,
  describeNoTestChannel,
  listTestChannels,
  selectTestChannel,
} from '../../components/test-surface';
import { getChannelsFromPypes } from '../../utils';
import { useProStudioCtxSelector } from '../../xstate/app.xstate';

import './test-view.scss';

/**
 * Test View — the Workflows tab, gated on `bobcat-test-view`.
 *
 * Its only job beyond hosting `<TestSurface>` is answering "which channel?".
 * The Workflows page knows a SOLUTION; Pype boots against a CHANNEL. Channel
 * Settings has one in scope already and passes it straight through, which is
 * why the surface takes `appId` rather than resolving it internally.
 *
 * `ctx.botPypes` is the current solution's pypes, so the lookup needs no fetch
 * of its own — and it is `null` until they load, which the empty state
 * distinguishes from a solution that genuinely has none.
 */
export const TestView: FC = () => {
  const botPypes = useProStudioCtxSelector((ctx) => ctx.botPypes);

  const channels = useMemo(
    () => (botPypes ? getChannelsFromPypes(botPypes) : null),
    [botPypes]
  );
  const testable = useMemo(() => listTestChannels(channels), [channels]);
  const defaultChannel = useMemo(() => selectTestChannel(channels), [channels]);

  // An explicit choice wins over the default, but only while it still names a
  // channel this solution has — switching solutions must not keep booting the
  // previous one's channel.
  const [chosenAppId, setChosenAppId] = useState<string | null>(null);
  const active =
    testable.find((channel) => channel.appId === chosenAppId) ?? defaultChannel;

  return (
    <div className="c-test-view" test-id="bobcat-test-view">
      <TestSurface
        appId={active?.appId ?? null}
        channelEnv={active?.channelEnv ?? 'sandbox'}
        channels={testable}
        onSelectChannel={setChosenAppId}
        noChannelReason={describeNoTestChannel(channels)}
      />
    </div>
  );
};
